import type { ComponentProps } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'react-use';

import type { DropdownOption } from '~/components/Dropdown';
import { FormFieldCombobox } from '~/components/FormField';
import { FEATURE_FLAGS } from '~/modules/experiments/features';
import { useFeatureFlags } from '~/modules/experiments/hooks/useFeatureFlags';
import type { PresentationLabelInfiniteListQueryVariables } from '~settings/api/queries/presentationLabelInfiniteList';
import { usePresentationLabelInfiniteList } from '~settings/api/queries/presentationLabelInfiniteList';
import { usePresentationLabelList } from '~settings/api/queries/presentationLabelList';
import { LARGE_DROPDOWN_PAGE_SIZE } from '~settings/constants';

interface SelectPresentationLabelProps
  extends Omit<
      ComponentProps<typeof FormFieldCombobox>,
      | 'clientSearch'
      | 'displaySelectedOptions'
      | 'label'
      | 'options'
      | 'onSearchChange'
      | 'search'
    >,
    Partial<Pick<ComponentProps<typeof FormFieldCombobox>, 'label'>> {}

export function SelectPresentationLabel(props: SelectPresentationLabelProps) {
  const { t } = useTranslation('settings');

  const [dropdownPaginationEnabled] = useFeatureFlags([
    FEATURE_FLAGS.ContainerSettingsDropdownPagination,
  ]);

  const [currentSearch, setCurrentSearch] = useState('');
  const [search, setSearch] = useState(currentSearch);

  useDebounce(
    () => {
      setSearch(currentSearch);
    },
    250,
    [currentSearch],
  );

  const extraParams: Partial<PresentationLabelInfiniteListQueryVariables> = {
    take: LARGE_DROPDOWN_PAGE_SIZE,
  };

  if (dropdownPaginationEnabled && search) {
    extraParams.search = search;
  }

  const { isLoading, presentationLabelList } = usePresentationLabelList(
    {
      skip: 0,
      ...extraParams,
    },
    {
      enabled: !dropdownPaginationEnabled,
    },
  );

  const {
    isLoading: isLoadingInfinite,
    presentationLabelInfiniteList,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = usePresentationLabelInfiniteList(extraParams, {
    enabled: dropdownPaginationEnabled,
  });

  const options = useMemo(() => {
    const presentationLabels =
      presentationLabelInfiniteList?.pages.flatMap((page) => page.data) ??
      presentationLabelList?.data ??
      [];

    // to make keys unique for React (API consumes a string)
    const uniqueNames = new Set();

    return presentationLabels.reduce<DropdownOption[]>((acc, next) => {
      if (next.id !== undefined && !uniqueNames.has(next.name)) {
        acc.push({
          value: next.name,
          label: next.name,
        });
        uniqueNames.add(next.name);
      }

      return acc;
    }, []);
  }, [presentationLabelInfiniteList?.pages, presentationLabelList?.data]);

  const onLoadMore = async () => {
    if (hasNextPage && dropdownPaginationEnabled && !isFetchingNextPage) {
      await fetchNextPage();
    }
  };

  return (
    <FormFieldCombobox
      label={t('label.presentationLabel')}
      placeholder={t('label.placeholder.presentationLabel')}
      searchPlaceholder={t('dropdowns.presentationLabel.searchPlaceholder')}
      {...props}
      clientSearch={!dropdownPaginationEnabled}
      onSearchChange={setCurrentSearch}
      options={options}
      readOnly={isLoading || isLoadingInfinite || props.readOnly}
      noOptionsText={t('dropdowns.presentationLabel.noOptions')}
      search={currentSearch}
      onLoadMore={onLoadMore}
      extendOptions
    />
  );
}

SelectPresentationLabel.displayName = 'SelectPresentationLabel';
